import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {TrustiiService} from 'src/app/services/trustii.service';
import {DialogService} from 'src/app/services/dialog.service';
import {SessionService} from 'src/app/services/session.service';
import {InviteMemberDialogComponent} from 'src/app/shared/invite-member-dialog/invite-member.dialog';
import {DataService, DatasetBannerModel, DatasetBannerView} from 'src/app/services/data.service';
// Rxjs
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, tap, filter, distinctUntilChanged} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-vendor-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['./teams.component.scss']
})
export class TeamsComponent implements OnInit {
  private params: Params;
  datasetId = 0;
  isReady = false;
  inProgress = false;
  requestInProgress = false;
  currentMember = {
    id: 0,
    name: '',
    isAdmin: 'no',
    userId: 0,
  };
  devMode = false;
  team: {
    name: string
  } = {
    name: ''
  };
  teamMembers: any[] = [];
  requests: any[] = [];
  invitations: any[] = [];
  public dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  public requestDataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  public invitationDataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  displayedColumns = ['lastname', 'firstname', 'isAdmin'];
  requestDisplayedColumns = ['name', 'status'];
  invitationDisplayedColumns = ['name', 'status', 'action'];

  constructor(private router: Router,
              private route: ActivatedRoute,
              private trustiiService: TrustiiService,
              private dialogService: DialogService,
              private matDialog: MatDialog,
              private sessionService: SessionService,
              private dataservice: DataService, public datasetBannerModel: DatasetBannerModel) {
  }

  /**
   * Init component
   *
   * @return void
   */
  ngOnInit(): void {
    this.datasetBannerModel.setBannerView(DatasetBannerView.TeamView);
    this.devMode = !environment.production;

    this._loadParams$()
      .pipe(
        switchMap(this._loadTeam$.bind(this)),
      )
      .subscribe();
  }

  /** _loadParams$ method
   *
   * Load params and set class properties.
   * @return Observable<Params>
   */
  private _loadParams$(): Observable<Params> {
    return this.route.params
      .pipe(
        map((params: Params) => this.params = params)
      );
  }

  /** _loadTeam$ method
   *
   * Load team.
   */
  private _loadTeam$(): Observable<any> {
    if (!this.params.datasetId || this.params.datasetId <= 0) {
      return of({});
    }
    this.datasetId = this.params.datasetId;
    return this.trustiiService.getVendorDatasetTeam(this.params.datasetId)
      .pipe(
        tap((res: any) => this.team = res.data.team),
        tap((res: any) => this.teamMembers = res.data.teamMembers),
        tap(() => {
          this.dataSource = new MatTableDataSource([]);
          this.dataSource.data = this.teamMembers;
        }),
        tap(() => this.isReady = true),
        tap(() => this.requestInProgress = false),
        tap(() => this.loadRequests()),
        tap(() => this.loadInvitations()),
        map((res: any) => res.data.teamMembers.filter((member) => member.userId === this.sessionService.getUser().roleId)),
        filter((member) => member.length > 0),
        tap((member) => this.currentMember = member[0]),
        catchError((error) => {
          this.isReady = true;
          this.requestInProgress = false;
          return throwError(error);
        })
      );
  }

  private loadRequests() {
    if (!this.params.datasetId || this.params.datasetId <= 0) {
      return of({});
    }
    this.datasetId = this.params.datasetId;
    this.trustiiService.datasetTeamRequestsMember(this.params.datasetId)
      .pipe(
        tap((res: any) => this.requests = res.data),
        tap(() => {
          this.requestDataSource = new MatTableDataSource([]);
          this.requestDataSource.data = this.requests;
        }),
        tap(() => this.isReady = true),
        catchError((error) => {
          this.isReady = true;
          return throwError(error);
        })
      ).subscribe();
  }

  private loadInvitations() {
    if (!this.params.datasetId || this.params.datasetId <= 0) {
      return of({});
    }
    this.datasetId = this.params.datasetId;
    this.trustiiService.datasetTeamInvitations(this.params.datasetId)
      .pipe(
        tap((res: any) => this.invitations = res.data),
        tap(() => {
          this.invitationDataSource = new MatTableDataSource([]);
          this.invitationDataSource.data = this.invitations;
        }),
        tap(() => this.isReady = true),
        catchError((error) => {
          this.isReady = true;
          return throwError(error);
        })
      ).subscribe();
  }

  updateName() {
    this.inProgress = true;
    this.trustiiService.updateTeam({name: this.team.name}, this.datasetId)
      .pipe(
        tap((res: any) => this.team = res.data),
        tap((res: any) => this.inProgress = false),
        catchError((error) => {
          return throwError(error);
        })
      ).subscribe();
  }

  inviteUser(): void {
    this.matDialog.open(InviteMemberDialogComponent, {
      height: '300px',
      width: '400px',
      data: {
        datasetId: this.datasetId,
      }
    }).afterClosed().pipe(
      distinctUntilChanged(),
      filter((result) => result && result.data === 'confirmation'),
      tap(() => this.loadRequests()),
    ).subscribe();
  }

  updateInvitation(id, status) {
    this.dialogService.openConfirmDialog('Are you sure you want to ' + status + '?').pipe(
      distinctUntilChanged(),
      filter((result) => result && result.data === 'confirmation'),
      tap(() => this.updateStatus(id, status))
    ).subscribe();
  }

  updateStatus(id, status) {
    this.requestInProgress = true;
    this.trustiiService.updateInvitation({status: status === 'accept' ? 'accepted' : 'rejected'}, this.datasetId, id)
      .pipe(
        switchMap(this._loadTeam$.bind(this)),
        catchError((error) => {
          return throwError(error);
        })
      ).subscribe();
  }
}
